import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    Typography,
} from "@mui/material";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { AbsoluteLoader } from "@common/Loader/AbsoluteLoader";
import { ACTIVE_CUSTOMER, ALLOW_3RD_PARTIES, NO_LONGER_CUSTOMER, TITLE_INHERIT_STYLE } from "@constants";
import "./css/ContactPreference.scss";
import { useContactPreferences } from "./hook/useContactPreferences";
import { HandleErrorAndLoading } from "@common/HandleErrorAndLoading/HandleErrorAndLoading";

export const ContactPreferencePage = () => {
    const {
        userContactPermissions,
        handleChange,
        permissionGroup,
        setPermissionGroup,
        getContactPreferenceListState,
        updateContactPreferenceListState,
        updateContactPreferences,
        getContactPreference,
    } = useContactPreferences();

    const { t } = useTranslation(["profile", "common"]);

    const isAnExistingCustomer = permissionGroup === ACTIVE_CUSTOMER;
    const isNoLongerCustomer = permissionGroup === NO_LONGER_CUSTOMER;
    const isThirdPartyAllowed = permissionGroup === ALLOW_3RD_PARTIES;

    return (
        <div className="contact-preferences">
            <Card elevation={2}>
                {(updateContactPreferenceListState.isLoading || getContactPreferenceListState.isLoading) && (
                    <AbsoluteLoader />
                )}
                <CardHeader
                    className="card-header card-header__right"
                    title={t("profile:myContactPreferences")}
                    titleTypographyProps={TITLE_INHERIT_STYLE}
                />
                <CardContent>
                    <div className="card-header__circle" />
                    <HandleErrorAndLoading
                        error={getContactPreferenceListState.error}
                        isLoading={false}
                        retryFetch={getContactPreference}
                    >
                        <Typography align="center" className="when-you-are" color="secondary" gutterBottom variant="h4">
                            {t("profile:whenYouAre")}
                        </Typography>
                        <Grid container justifyContent="center" spacing={2}>
                            <Grid item>
                                <Button
                                    className={!isAnExistingCustomer ? "contact-preferences__button-disabled" : ""}
                                    color={isAnExistingCustomer ? "primary" : "inherit"}
                                    variant={isAnExistingCustomer ? "contained" : "outlined"}
                                    onClick={() => setPermissionGroup(ACTIVE_CUSTOMER)}
                                >
                                    {t("profile:anExistingCustomer")}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    className={!isNoLongerCustomer ? "contact-preferences__button-disabled" : ""}
                                    color={isNoLongerCustomer ? "primary" : "inherit"}
                                    variant={isNoLongerCustomer ? "contained" : "outlined"}
                                    onClick={() => setPermissionGroup(NO_LONGER_CUSTOMER)}
                                >
                                    {t("profile:noLongerCustomer")}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    className={!isThirdPartyAllowed ? "contact-preferences__button-disabled" : ""}
                                    color={isThirdPartyAllowed ? "primary" : "inherit"}
                                    variant={isThirdPartyAllowed ? "contained" : "outlined"}
                                    onClick={() => setPermissionGroup(ALLOW_3RD_PARTIES)}
                                >
                                    {t("profile:thirdParty")}
                                </Button>
                            </Grid>
                        </Grid>
                        {updateContactPreferenceListState.error && (
                            <Grid className="contact-preferences__list" container justifyContent="center" spacing={2}>
                                <Grid item xs={9}>
                                    <Typography align="center" color="error" variant="small">
                                        {t("common:genericError")}
                                    </Typography>
                                </Grid>
                            </Grid>
                        )}
                        {userContactPermissions.map((field, index) => (
                            <Fragment key={field.name}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.isEnabled}
                                            color={"secondary"}
                                            name={field.name}
                                            value={field.permission}
                                            onChange={(event) => handleChange(event.target.value)}
                                        />
                                    }
                                    label={field.name}
                                />
                                {index !== userContactPermissions.length - 1 && <Divider />}
                            </Fragment>
                        ))}
                        <Grid container justifyContent="center" spacing={2}>
                            <Grid item>
                                <Button color="primary" type="button" variant="outlined" onClick={getContactPreference}>
                                    REFRESH
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    color="primary"
                                    type="button"
                                    variant="outlined"
                                    onClick={updateContactPreferences}
                                >
                                    SAVE
                                </Button>
                            </Grid>
                        </Grid>
                    </HandleErrorAndLoading>
                </CardContent>
            </Card>
        </div>
    );
};
